/** Crown Institute Academy — shared login component
 *  Role-specific sign-in that routes to /portal after auth. Enforces expected role.
 */
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { auth, api } from "../lib/api";
import { fetchDemoCredentials } from "../lib/demoCredentials";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { Crown, ShieldCheck, ArrowRight } from "lucide-react";

const CREST = "/images/crown_institute_crest.png";

const CrownLogin = ({ expectedRole, title, subtitle, demoKey, testid }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [demoEmail, setDemoEmail] = useState("");
  const [demoPassword, setDemoPassword] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    fetchDemoCredentials().then(d => {
      const c = (d.crown || {})[demoKey] || {};
      setDemoEmail(c.email || "");
      setDemoPassword(c.password || "");
    });
  }, [demoKey]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await auth.login(email, password);
      auth.saveToken(r.access_token);
      // Validate role matches
      const me = await api.get("/portal/me").then(x => x.data);
      if (me.role !== expectedRole && me.role !== "super_admin") {
        auth.clear();
        toast.error(`This login is for ${title}s only. Please use the correct login page.`);
        setBusy(false);
        return;
      }
      toast.success(`Welcome, ${me.name}`);
      // Route by role
      if (me.role === "student")       nav("/portal/student");
      else if (me.role === "teacher_aide") nav("/portal/teacher-aide");
      else if (me.role === "parent")   nav("/portal/parent");
      else                              nav("/portal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    }
    setBusy(false);
  };

  const useDemo = () => { setEmail(demoEmail); setPassword(demoPassword); };

  return (
    <Layout>
      <SEOHead title={`${title} Login | Crown Institute Academy`} path={`/${expectedRole}-login`} />
      <Toaster position="top-right" richColors theme="dark" />
      <section className="relative py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(201,168,76,0.12),transparent_60%)]" />
        <div className="container-x relative max-w-5xl grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            <img src={CREST} alt="Crown Institute Academy crest" className="h-32 mb-6" />
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-gold">
              <Crown size={12} /> Crown Institute Academy
            </div>
            <h1 className="font-display text-4xl md:text-5xl mt-4">{title} Login</h1>
            <p className="text-cream-dim mt-4 max-w-lg">{subtitle}</p>
            <Link to="/crown-institute-academy"
              className="inline-flex items-center gap-2 text-electric hover:text-gold text-sm mt-6">
              ← Back to Academy
            </Link>
          </div>

          <div className="glass p-8" data-testid={testid}>
            <div className="flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-electric">
              <ShieldCheck size={14} /> Secure Login
            </div>
            <h2 className="font-display text-2xl mt-2">Authenticate</h2>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid={`${testid}-form`}>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Email</label>
                <Input required type="email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="mt-1 bg-navy-deep border-gold-dim/40"
                  data-testid={`${testid}-email`} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Password</label>
                <Input required type="password" value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="mt-1 bg-navy-deep border-gold-dim/40"
                  data-testid={`${testid}-password`} />
              </div>
              <Button disabled={busy} className="btn-gold h-11 w-full" data-testid={`${testid}-submit`}>
                {busy ? "Signing in…" : <>Sign In Securely <ArrowRight size={14} className="ml-2" /></>}
              </Button>
            </form>

            <button onClick={useDemo}
              data-testid={`${testid}-quickfill`}
              className="w-full mt-5 p-3 rounded border border-gold-dim/30 hover:border-gold text-left transition-colors">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gold">Try demo account</div>
              <div className="font-mono text-xs text-cream mt-1">{demoEmail}</div>
              <code className="text-[10px] text-steel">{demoPassword}</code>
            </button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

// Demo credentials are fetched at runtime from /api/demo/credentials so no
// account literals are present in client source. Demo accounts gate access
// to a synthetic dataset only.
export const StudentLogin = () => <CrownLogin
  expectedRole="student"
  title="Student"
  subtitle="Access your courses, lessons, assignments, exams, grades and internship hours."
  demoKey="student"
  testid="student-login" />;

export const TeacherAideLogin = () => <CrownLogin
  expectedRole="teacher_aide"
  title="Teacher's Aide"
  subtitle="View your student roster, grade assignments, review exam results, and track internship logs."
  demoKey="teacher_aide"
  testid="teacher-aide-login" />;

export const ParentLogin = () => <CrownLogin
  expectedRole="parent"
  title="Parent"
  subtitle="See your child's progress, grades, remediation alerts, attendance, and internship status."
  demoKey="parent"
  testid="parent-login" />;

export default CrownLogin;
